import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { DoseCalcModeActive, DoseLimitsMatrix, OverdosePreventionLevel, RoundingMode, CompoundMappingType, } from '../../database/schema.js';
import { queries } from '../../queries.js';
import {} from '../../schema.js';
import { itemsDetail, statusItems } from '../../util/event-log-items.js';
import { pickActorFromUserActions, pickTimestampFromUserActions, titleCase } from '../../util/index.js';
import { Status } from '../../util/schemas.js';
import { assertCan } from '../../utils.js';
import { assertDoseUnitUnlocked } from './dose-unit-lock.js';
import { DeleteResourceSchema, EventLogResources, UpdateResourceStatusSchema, addEventLog, } from '../util.js';
import { AddDrugItemSchema, createDrugCatalogItem, createDrugItemCategories, createMedicationProfiles, replaceDrugItemCategories, replaceMedicationProfiles, updateDrugCatalogItem, } from './utils/index.js';
import { upsertCompoundMapping } from '../compounders/utils.js';
export const SupportiveMedicationSchema = AddDrugItemSchema.omit({ catalogType: true }).extend({
    doseCalcMode: DoseCalcModeActive.nonoptional(),
    doseUnitId: z.uuidv7().nonempty(),
    doseLimits: DoseLimitsMatrix.nonoptional(),
    precision: z.number().gte(0).nonoptional(),
    roundingMode: RoundingMode.nonoptional(),
    overdosePreventionLevel: OverdosePreventionLevel.nonoptional(),
    externalInformationLink: z.url().or(z.literal('')).optional(),
});
export const AddSupportiveMedicationSchema = SupportiveMedicationSchema;
export const UpdateSupportiveMedicationSchema = SupportiveMedicationSchema.partial().extend({
    id: z.uuidv7().nonempty(),
});
async function findById(tx, ctx, id) {
    return await tx.run(queries.drugCatalog.getSupportiveMedicationById.fn({ args: id, ctx }));
}
async function createSupportiveMedicationDetails(tx, args, userActions) {
    const actor = pickActorFromUserActions(userActions);
    const timestamp = pickTimestampFromUserActions(userActions);
    const newSupportiveMedication = {
        drugCatalogItemId: args.drugItemId,
        doseCalcMode: args.doseCalcMode,
        doseUnitId: args.doseUnitId,
        dosePrecision: args.precision,
        doseLimits: args.doseLimits,
        roundingMode: args.roundingMode,
        overdosePreventionLevel: args.overdosePreventionLevel,
        externalInformationLink: args.externalInformationLink,
        createdAt: timestamp,
        createdBy: actor,
    };
    const promises = [tx.mutate.supportiveMedicationDetails.insert(newSupportiveMedication)];
    if (tx.location === 'server') {
        const patches = createPatch({}, newSupportiveMedication);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.supportiveMedicationDetails,
            resourceId: newSupportiveMedication.drugCatalogItemId,
            action: 'create',
            patches: patches,
            performedBy: actor,
            performedAt: timestamp,
            status: 'OK',
            details: { id: newSupportiveMedication.drugCatalogItemId, name: titleCase(args.name) },
        }));
    }
    await Promise.all(promises);
    return newSupportiveMedication;
}
async function updateSupportiveMedicationDetails(tx, prev, args, userActions) {
    const actor = pickActorFromUserActions(userActions);
    const timestamp = pickTimestampFromUserActions(userActions);
    const updatedSupportiveMedication = {
        drugCatalogItemId: args.id,
        doseCalcMode: args.doseCalcMode,
        doseUnitId: args.doseUnitId,
        dosePrecision: args.precision,
        doseLimits: args.doseLimits,
        roundingMode: args.roundingMode,
        overdosePreventionLevel: args.overdosePreventionLevel,
        externalInformationLink: args.externalInformationLink,
        updatedAt: timestamp,
        updatedBy: actor,
    };
    const promises = [tx.mutate.supportiveMedicationDetails.update(updatedSupportiveMedication)];
    if (tx.location === 'server') {
        const patches = createPatch(prev.supportiveMedicationDetails, updatedSupportiveMedication);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.supportiveMedicationDetails,
            resourceId: updatedSupportiveMedication.drugCatalogItemId,
            action: 'update',
            patches: patches,
            performedBy: actor,
            performedAt: timestamp,
            status: 'OK',
            details: { id: args.id, name: prev.name },
        }));
    }
    await Promise.all(promises);
    return updatedSupportiveMedication;
}
export const supportiveMedications = {
    add: defineMutator(AddSupportiveMedicationSchema, async ({ ctx, tx, args }) => {
        assertCan(ctx, 'catalogue.manage');
        const now = Date.now();
        const userActions = {
            createdAt: now,
            createdBy: ctx.user.id,
        };
        const newDrugItem = await createDrugCatalogItem(tx, { ...args, catalogType: 'SUPPORTIVE_MEDICATION' }, userActions);
        await createSupportiveMedicationDetails(tx, { ...args, drugItemId: newDrugItem.id }, userActions);
        await createDrugItemCategories(tx, { categories: args.categoriesIds, drugItemId: newDrugItem.id }, userActions);
        await createMedicationProfiles(tx, { medicationProfiles: args.medicationProfiles, drugItemId: newDrugItem.id }, userActions);
        await upsertCompoundMapping(tx, {
            mappingType: CompoundMappingType.enum.DRUG,
            sourceId: newDrugItem.id,
            machineId: args.machineId,
            actorId: ctx.user.id,
            now,
        });
    }),
    update: defineMutator(UpdateSupportiveMedicationSchema, async ({ ctx, tx, args }) => {
        assertCan(ctx, 'catalogue.manage');
        const prev = await findById(tx, ctx, args.id);
        if (!prev)
            throw new Error('Not Found');
        const now = Date.now();
        const userActions = {
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await updateDrugCatalogItem(tx, prev, args, userActions);
        await assertDoseUnitUnlocked(tx, {
            substanceId: args.id,
            substanceName: prev.name,
            previousUnitId: prev.supportiveMedicationDetails?.doseUnitId,
            nextUnitId: args.doseUnitId,
        });
        await updateSupportiveMedicationDetails(tx, prev, args, userActions);
        await replaceDrugItemCategories(tx, { drugItem: prev, newDrugCategoriesIds: args.categoriesIds || [] }, userActions);
        await replaceMedicationProfiles(tx, { drugItem: prev, newMedicationProfiles: args.medicationProfiles || [] }, userActions);
        await upsertCompoundMapping(tx, {
            mappingType: CompoundMappingType.enum.DRUG,
            sourceId: args.id,
            machineId: args.machineId,
            actorId: ctx.user.id,
            now,
        });
    }),
    updateStatus: defineMutator(UpdateResourceStatusSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'catalogue.manage');
        const prev = await findById(tx, ctx, args.id);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const updatedDrugItem = {
            id: args.id,
            status: args.status,
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.drugCatalogItems.update(updatedDrugItem);
        if (tx.location === 'server') {
            const patches = createPatch({ status: prev.status }, { status: args.status });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.drugCatalogItem,
                resourceId: args.id,
                action: 'update',
                patches: patches,
                performedBy: updatedDrugItem.updatedBy,
                performedAt: updatedDrugItem.updatedAt,
                status: args.status === Status.enum.inactive ? 'WARNING' : 'INFO',
                details: { id: args.id, name: prev.name, ...itemsDetail(statusItems(prev.status, args.status)) },
            }, ctx);
        }
    }),
    delete: defineMutator(DeleteResourceSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'catalogue.manage');
        const prev = await findById(tx, ctx, args.id);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const updatedDrug = {
            id: args.id,
            code: `${prev.code}_deleted_${now}`,
            deletedAt: now,
            deletedBy: ctx.user.id,
        };
        await tx.mutate.drugCatalogItems.update(updatedDrug);
        if (tx.location === 'server') {
            const patches = createPatch({ code: prev.code }, { code: updatedDrug.code });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.drugCatalogItem,
                resourceId: args.id,
                action: 'delete',
                patches: patches,
                performedBy: updatedDrug.deletedBy,
                performedAt: updatedDrug.deletedAt,
                status: 'WARNING',
                details: { id: args.id, name: prev.name },
            }, ctx);
        }
    }),
};
//# sourceMappingURL=supportive-medication.mutators.js.map